'use client';

import { c as _c } from "react/compiler-runtime";
import Script from 'next/script';
import { getEnv } from '@/utils/env';

/**
 * `appcues` gates the Appcues device-mode destination, which Segment loads on our behalf.
 *
 * Appcues is not referenced anywhere in this codebase: it arrives purely as a Segment destination, so
 * the ONLY place it can be switched off from the app is right here in the load options. On the public
 * Labs preview an Appcues product tour firing mid-lab would be the single most "this is an app I never
 * signed up for" event available, and it is a third-party script running for a visitor who has
 * consented to nothing.
 */
export function SegmentScript(t0) {
  const $ = _c(3);
  const {
    appcues: t1
  } = t0;
  const appcues = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getEnv("SEGMENT_WRITE_KEY");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const writeKey = t2;
  const t3 = `
      (function() {

        function getGAClientId() {
          const gaCookie = document.cookie.split('; ').find((row) => row.startsWith('_ga='));

          if (gaCookie) {
            const matches = gaCookie.match(/GA1.[0-9]+.([^;]+)/);
            if (matches) {
              return matches[1];
            }
          }

          return null;
        }

        // define the key where the global analytics object will be accessible
        // customers can safely set this to be something else if need be
        var globalAnalyticsKey = "analytics"

        // Create a queue, but don't obliterate an existing one!
        var analytics = window[globalAnalyticsKey] = window[globalAnalyticsKey] || [];

        // If the real analytics.js is already on the page return.
        if (analytics.initialize) return;

        // If the snippet was invoked already show an error.
        if (analytics.invoked) {
          if (window.console && console.error) {
            console.error("Segment snippet included twice.");
          }
          return;
        }

        // Invoked flag, to make sure the snippet
        // is never invoked twice.
        analytics.invoked = true;

        // A list of the methods in Analytics.js to stub.
        analytics.methods = [
          "trackSubmit",
          "trackClick",
          "trackLink",
          "trackForm",
          "pageview",
          "identify",
          "reset",
          "group",
          "track",
          "ready",
          "alias",
          "debug",
          "page",
          "screen",
          "once",
          "off",
          "on",
          "addSourceMiddleware",
          "addIntegrationMiddleware",
          "setAnonymousId",
          "addDestinationMiddleware",
          "register"
        ];

        analytics.factory = function(e) {
          return function() {
            if (window[globalAnalyticsKey].initialized) {
              // Sometimes users assigned analytics to a variable before analytics is done loading, resulting in a stale reference.
              // If so, proxy any calls to the 'real' analytics instance.
              return window[globalAnalyticsKey][e].apply(window[globalAnalyticsKey], arguments);
            }
            var args = Array.prototype.slice.call(arguments);

            // Add buffered page context object so page information is always up-to-date
            if (["track", "screen", "alias", "group", "page", "identify"].indexOf(e) > -1) {
              var c = document.querySelector("link[rel='canonical']");
              args.push({
                __t: "bpc",
                c: c && c.getAttribute("href") || undefined,
                p: location.pathname,
                u: location.href,
                s: location.search,
                t: document.title,
                r: document.referrer
              });
            }

            args.unshift(e);
            analytics.push(args);
            return analytics;
          };
        };

        // For each of our methods, generate a queueing stub.
        for (var i = 0; i < analytics.methods.length; i++) {
          var key = analytics.methods[i];
          analytics[key] = analytics.factory(key);
        }

        // Define a method to load Analytics.js from our CDN,
        // and that will be sure to only ever load it once.
        analytics.load = function(key, options) {
          // Create an async script element based on your key.
          var t = document.createElement("script");
          t.type = "text/javascript";
          t.async = true;
          t.setAttribute("data-global-segment-analytics-key", globalAnalyticsKey)
          t.src = "https://segment-analytics.extern.com/analytics.js/v1/" + key + "/analytics.min.js";

          // Insert our script next to the first script element.
          var first = document.getElementsByTagName("script")[0];
          first.parentNode.insertBefore(t, first);
          analytics._loadOptions = options;
        };

        analytics._writeKey = "${writeKey}";

        // Add a version to keep track of what's in the wild.
        analytics.SNIPPET_VERSION = "5.2.1";

        // Load Analytics.js with your key, which will automatically
        // load the tools you've enabled for your account. Boosh!
        analytics.load("${writeKey}",
        {
          integrations: {
            'Segment.io': {
              apiHost: 'segment-api.extern.com/v1',
              protocol: 'https',
            },
            Appcues: ${appcues ? "true" : "false"},
          },
        });

        analytics.addSourceMiddleware(({ payload, next }) => {
          if (['track', 'page', 'identify'].includes(payload.obj.type)) {
            payload.obj.properties = { ...payload.obj.properties, ga_client_id: this.gaClientId ?? getGAClientId() };
          }
          next(payload);
        });
      })();
    `;
  let t4;
  if ($[1] !== t3) {
    t4 = <Script id="segment-script">{t3}</Script>;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  return t4;
}